import React, { useState, useEffect, useMemo } from "react";
import hosys from "@/api/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle, CheckCircle2, Clock, XCircle, Users, RefreshCw,
  ChevronLeft, ChevronRight, Pencil, PlusCircle, ShieldAlert, Loader2
} from "lucide-react";
import moment from "moment";
import "moment/locale/ca";
import {
  getJourneyState, getSuggestedActions, calcWorkedMinutes,
  minutesToHHMM, getTypeLabel, STATE_CONFIG, JOURNEY_STATES
} from "./journeyEngine";
import EditDayModalLegal from "./EditDayModalLegal";
moment.locale("ca");

export default function SupervisorDashboard({ venueId, members }) {
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [now, setNow] = useState(new Date());
  const [tab, setTab] = useState("all"); // all | working | issues | absent

  useEffect(() => {
    hosys.auth.me().then(setCurrentUser).catch(() => {});
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!venueId) return;
    loadRecords();
  }, [venueId, selectedDate]);

  // Real-time subscription
  useEffect(() => {
    if (!venueId) return;
    const unsub = hosys.entities.TimeRecord.subscribe((event) => {
      if (event.data?.venue_id === venueId && event.data?.date === selectedDate) {
        loadRecords();
      }
    });
    return unsub;
  }, [venueId, selectedDate]);

  const loadRecords = async () => {
    setLoading(true);
    const prevDate = moment(selectedDate).subtract(1, "day").format("YYYY-MM-DD");
    const all = await hosys.entities.TimeRecord.filter({
      venue_id: venueId,
      date_from: prevDate,
      date_to: selectedDate,
      limit: 2000,
    });
    setRecords(all);
    setLoading(false);
  };

  const getMemberRecords = (email) => {
    const prevDate = moment(selectedDate).subtract(1, "day").format("YYYY-MM-DD");
    const midnightSelected = moment(selectedDate).startOf("day");
    const memberRecs = records.filter(r => r.user_email === email);
    const prevRecs = memberRecs.filter(r => r.date === prevDate && !r.is_deleted);
    
    // Check if yesterday's shift extends into today:
    // 1. Shift still open (last record is entry/pause)
    // 2. Exit happened past midnight (timestamp >= midnight of selected day)
    const hasOpenPrev = (() => {
      const sorted = [...prevRecs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      if (!sorted.length) return false;
      const last = sorted[sorted.length - 1];
      // Shift still open
      if (last.type === "entry" || last.type === "pause_start" || last.type === "pause_end") return true;
      // Exit happened past midnight (timestamp is on or after the selected day)
      if (last.type === "exit" && moment(last.timestamp).isSameOrAfter(midnightSelected)) return true;
      return false;
    })();
    const todayRecs = memberRecs.filter(r => r.date === selectedDate);
    return hasOpenPrev
      ? [...prevRecs, ...todayRecs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      : todayRecs;
  };

  const isToday = selectedDate === moment().format("YYYY-MM-DD");
  const prevDay = () => setSelectedDate(d => moment(d).subtract(1, "day").format("YYYY-MM-DD"));
  const nextDay = () => setSelectedDate(d => moment(d).add(1, "day").format("YYYY-MM-DD"));

  // Classify members
  const membersWithState = useMemo(() => members.map(m => {
    const recs = getMemberRecords(m.user_email);
    const journey = getJourneyState(recs);
    return { ...m, journey, recs };
  }), [members, records, selectedDate]);

  const { working, onBreak, finished, absent, issues, totalHours } = useMemo(() => {
    const working = membersWithState.filter(m => m.journey.state === JOURNEY_STATES.WORKING);
    const onBreak = membersWithState.filter(m => m.journey.state === JOURNEY_STATES.ON_BREAK);
    const finished = membersWithState.filter(m => m.journey.state === JOURNEY_STATES.FINISHED);
    const absent = membersWithState.filter(m => m.journey.state === JOURNEY_STATES.NOT_STARTED);
    const issues = membersWithState.filter(m =>
      m.journey.state === JOURNEY_STATES.INCONSISTENT ||
      m.journey.issues.length > 0
    );
    const totalHours = membersWithState.reduce((acc, m) => acc + m.journey.workedMinutes, 0);
    return { working, onBreak, finished, absent, issues, totalHours };
  }, [membersWithState]);

  const filterMap = {
    all: membersWithState,
    working: [...working, ...onBreak],
    issues: issues,
    absent: [...absent, ...finished],
  };
  const displayMembers = filterMap[tab] || membersWithState;

  return (
    <div className="space-y-4">
      {/* Date navigator */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 shadow-sm p-3">
        <button onClick={prevDay} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="text-center">
          <p className="font-bold text-gray-900 capitalize text-sm">{moment(selectedDate).format("dddd, D MMMM YYYY")}</p>
          {isToday && <p className="text-xs text-[#7B2D3F] font-medium">Avui</p>}
        </div>
        <div className="flex items-center gap-1">
          {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
          <button onClick={nextDay} disabled={isToday} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-30">
            <ChevronRight className="w-5 h-5" />
          </button>
          <button onClick={loadRecords} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-4 gap-2">
        <button onClick={() => setTab("working")} className={`rounded-xl p-3 text-center border transition-all ${tab === "working" ? "ring-2 ring-emerald-400" : ""} bg-emerald-50 border-emerald-100`}>
          <p className="text-xl font-bold text-emerald-700">{working.length + onBreak.length}</p>
          <p className="text-[10px] text-emerald-600 font-medium">Actius</p>
        </button>
        <button onClick={() => setTab("absent")} className={`rounded-xl p-3 text-center border transition-all ${tab === "absent" ? "ring-2 ring-gray-400" : ""} bg-gray-50 border-gray-200`}>
          <p className="text-xl font-bold text-gray-500">{absent.length}</p>
          <p className="text-[10px] text-gray-500 font-medium">Absents</p>
        </button>
        <button onClick={() => setTab("issues")} className={`rounded-xl p-3 text-center border transition-all ${tab === "issues" ? "ring-2 ring-red-400" : ""} bg-red-50 border-red-100`}>
          <p className="text-xl font-bold text-red-600">{issues.length}</p>
          <p className="text-[10px] text-red-500 font-medium">Incidències</p>
        </button>
        <button onClick={() => setTab("all")} className={`rounded-xl p-3 text-center border transition-all ${tab === "all" ? "ring-2 ring-[#7B2D3F]/40" : ""} bg-[#7B2D3F]/5 border-[#7B2D3F]/10`}>
          <p className="text-xl font-bold text-[#7B2D3F]">{minutesToHHMM(totalHours)}</p>
          <p className="text-[10px] text-[#7B2D3F] font-medium">Total hores</p>
        </button>
      </div>

      {/* Members list */}
      <div className="space-y-2">
        {displayMembers.length === 0 && (
          <div className="text-center py-8 text-gray-400 text-sm bg-white rounded-xl border border-gray-100">
            {tab === "issues" ? "Cap incidència detectada ✓" : "Cap treballador en aquest filtre"}
          </div>
        )}
        {displayMembers.map(member => {
          const { journey } = member;
          const cfg = STATE_CONFIG[journey.state];
          const hasIssues = journey.issues.length > 0;

          return (
            <div key={member.id} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${hasIssues ? "border-red-200" : "border-gray-100"}`}>
              <div className="flex items-center gap-3 p-3">
                {/* Avatar */}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.bgCard}`}>
                  <span className={`font-bold text-sm ${cfg.textColor}`}>
                    {(member.user_name || member.user_email).charAt(0).toUpperCase()}
                  </span>
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-800 text-sm">{member.user_name || member.user_email}</span>
                    {hasIssues && <ShieldAlert className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cfg.color}`}>
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${cfg.dot} mr-1`} />
                      {cfg.label}
                    </span>
                    {journey.workedMinutes > 0 && (
                      <span className="text-xs font-bold text-gray-700">{minutesToHHMM(journey.workedMinutes)}</span>
                    )}
                    {journey.entryTime && (
                      <span className="text-[10px] text-gray-400">
                        Entrada: {moment(journey.entryTime).format("HH:mm")}
                      </span>
                    )}
                  </div>
                </div>
                {/* Edit button */}
                <button
                  onClick={() => setEditingMember(member)}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-[#7B2D3F] transition-colors flex-shrink-0"
                  title="Editar fitxatges"
                >
                  {journey.state === JOURNEY_STATES.NOT_STARTED
                    ? <PlusCircle className="w-4 h-4" />
                    : <Pencil className="w-4 h-4" />
                  }
                </button>
              </div>

              {/* Issues */}
              {hasIssues && (
                <div className="px-3 pb-3 space-y-1">
                  {journey.issues.map((issue, i) => (
                    <div key={`issue-${i}`} className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2 ${issue.severity === "error" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                      {issue.msg}
                    </div>
                  ))}
                </div>
              )}

              {/* Records timeline (only if has records) */}
              {member.recs.filter(r => !r.is_deleted).length > 0 && (
                <div className="px-3 pb-3 border-t border-gray-50 pt-2">
                  <div className="flex gap-2 flex-wrap">
                    {member.recs
                      .filter(r => !r.is_deleted)
                      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
                      .map(r => {
                        const colors = {
                          entry: "bg-emerald-100 text-emerald-700",
                          exit: "bg-rose-100 text-rose-700",
                          pause_start: "bg-amber-100 text-amber-700",
                          pause_end: "bg-blue-100 text-blue-700",
                        };
                        return (
                          <span key={r.id} className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${colors[r.type]}`}>
                            {getTypeLabel(r.type)} {moment(r.timestamp).format("HH:mm")}
                          </span>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editingMember && currentUser && (
        <EditDayModalLegal
          member={editingMember}
          date={selectedDate}
          records={records.filter(r => r.user_email === editingMember.user_email && !r.is_deleted)}
          venueId={venueId}
          currentUser={currentUser}
          onClose={() => setEditingMember(null)}
          onSaved={() => { setEditingMember(null); loadRecords(); }}
        />
      )}
    </div>
  );
}